import React, { useState, useCallback } from 'react';
import { Container, Row, Col, Button, Form, ListGroup, Card, Navbar, Nav } from 'react-bootstrap';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';

const App = () => {
  const [files, setFiles] = useState([]);
  const [theme, setTheme] = useState('light');

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles([...files, ...selectedFiles]);
  };

  const handleFileDrop = useCallback((e) => {
    e.preventDefault();
    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles([...files, ...droppedFiles]);
  }, [files]);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const removeFile = (index) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const moveFile = (fromIndex, toIndex) => {
    const newFiles = [...files];
    const [movedFile] = newFiles.splice(fromIndex, 1);
    newFiles.splice(toIndex, 0, movedFile);
    setFiles(newFiles);
  };

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const convertToPdf = async () => {
    const pdf = new jsPDF();
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileType = file.type;

      if (i > 0) {
        pdf.addPage();
      }

      if (fileType.startsWith('image/')) {
        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = (e) => {
            const imgData = e.target.result;
            const img = new Image();
            img.src = imgData;
            img.onload = () => {
              const { width, height } = img;
              const pdfWidth = pdf.internal.pageSize.getWidth();
              const pdfHeight = pdf.internal.pageSize.getHeight();
              const ratio = Math.min(pdfWidth / width, pdfHeight / height);
              const imgWidth = width * ratio;
              const imgHeight = height * ratio;
              pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
              resolve();
            };
          };
          reader.readAsDataURL(file);
        });
      } else if (
        fileType.startsWith('text/') ||
        file.name.endsWith('.js') ||
        file.name.endsWith('.css') ||
        file.name.endsWith('.html')
      ) {
        const reader = new FileReader();
        await new Promise((resolve) => {
          reader.onload = (e) => {
            const text = e.target.result;
            const element = document.createElement('div');
            element.innerText = text;
            document.body.appendChild(element);
            html2canvas(element).then((canvas) => {
              const imgData = canvas.toDataURL('image/png');
              const pdfWidth = pdf.internal.pageSize.getWidth();
              const pdfHeight = pdf.internal.pageSize.getHeight();
              pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);
              document.body.removeChild(element);
              resolve();
            });
          };
          reader.readAsText(file);
        });
      } else {
        pdf.text(`File: ${file.name}`, 10, 10);
        pdf.text(`Type: ${fileType}`, 10, 20);
        pdf.text('This file type is not supported for direct conversion.', 10, 30);
      }
    }
    pdf.save('converted.pdf');
  };

  return (
    <div className={`app-container ${theme}`}>
      <Navbar bg={theme} variant={theme} expand="lg" className="shadow-sm">
        <Container>
          <Navbar.Brand href="#home">PDF Converter</Navbar.Brand>
          <Nav className="ms-auto">
            <Button variant="outline-secondary" onClick={toggleTheme}>
              {theme === 'light' ? 'Dark' : 'Light'} Mode
            </Button>
          </Nav>
        </Container>
      </Navbar>

      <Container className="mt-5">
        <Row>
          <Col>
            <Card className="shadow-sm">
              <Card.Body>
                <h2 className="text-center mb-4">All-in-One PDF Converter</h2>
                <div
                  className="drop-zone text-center p-5 mb-4"
                  onDrop={handleFileDrop}
                  onDragOver={handleDragOver}
                >
                  <p>Drag and drop files here, or click to select files.</p>
                  <Form.Control type="file" multiple onChange={handleFileChange} />
                </div>
                <div className="text-center">
                  <Button
                    variant="primary"
                    size="lg"
                    onClick={convertToPdf}
                    disabled={files.length === 0}
                  >
                    Convert to PDF
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {files.length > 0 && (
          <Row className="mt-5">
            <Col>
              <Card className="shadow-sm">
                <Card.Body>
                  <h3 className="mb-3">File Queue</h3>
                  <ListGroup>
                    {files.map((file, index) => (
                      <ListGroup.Item
                        key={index}
                        className="d-flex justify-content-between align-items-center"
                      >
                        <span>{file.name}</span>
                        <div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => moveFile(index, index - 1)}
                            disabled={index === 0}
                          >
                            Up
                          </Button>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            className="me-2"
                            onClick={() => moveFile(index, index + 1)}
                            disabled={index === files.length - 1}
                          >
                            Down
                          </Button>
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => removeFile(index)}
                          >
                            Remove
                          </Button>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    </div>
  );
};

export default App;